import type { RaRecord } from "react-admin";
import { isYearlySabilType } from "@/constants";
import { formatListDate } from "@/utils/date-format";

type SabilClosedInput = { closedAt?: unknown } | RaRecord | undefined;

type SabilTakhmeenStartInput =
  | {
      sabilTakhmeenCurrent?: { startDate?: unknown; createdAt?: unknown } | null;
    }
  | RaRecord
  | undefined;

export type LastPaidMonth = {
  month: number;
  year: number;
  status?: string;
};

const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const FULL_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export function isSabilClosed(record: SabilClosedInput): boolean {
  return Boolean((record as { closedAt?: unknown } | undefined)?.closedAt);
}

export function resolveSabilStatusLabel(closedAt: unknown): "Active" | "Closed" {
  return closedAt ? "Closed" : "Active";
}

export function isEstablishmentOrProfessionalSabilType(
  sabilType: string | null | undefined
): boolean {
  return sabilType === "ESTABLISHMENT" || sabilType === "PROFESSIONAL";
}

/** Ledger period label; yearly rows use April YYYY to March YYYY+1. */
export function formatLedgerPeriodLabel(
  month: number | null | undefined,
  year: number | null | undefined,
  sabilType: string | null | undefined
): string {
  if (month == null || year == null) {
    return "—";
  }

  if (isYearlySabilType(sabilType) && month === 4) {
    return `${FULL_MONTH_NAMES[3]} ${year} to ${FULL_MONTH_NAMES[2]} ${year + 1}`;
  }

  const monthName = SHORT_MONTH_NAMES[month - 1] ?? String(month);
  return `${monthName} - ${year}`;
}

export function formatBalanceLastPaidLabel(
  lastPaidMonth: LastPaidMonth | null | undefined,
  sabilType: string | null | undefined
): string {
  if (!lastPaidMonth || lastPaidMonth.status === "UNPAID") {
    return "Never paid";
  }

  return formatLedgerPeriodLabel(lastPaidMonth.month, lastPaidMonth.year, sabilType);
}

export function formatSabilLastPaidDate(
  lastPaidDate: unknown,
  sabilType: string | null | undefined,
  options?: { empty?: string }
): string {
  const empty = options?.empty ?? "Never paid";
  if (!lastPaidDate) {
    return empty;
  }

  if (isYearlySabilType(sabilType)) {
    const d = new Date(lastPaidDate as string | number | Date);
    const month = d.getUTCMonth() + 1;
    const year = d.getUTCFullYear();
    if (month === 4) {
      return `${FULL_MONTH_NAMES[3]} ${year} to ${FULL_MONTH_NAMES[2]} ${year + 1}`;
    }
  }

  return formatListDate(lastPaidDate as string | number | Date, { empty });
}

export function formatTakhmeenStartDate(record: SabilTakhmeenStartInput): string {
  const startDate = (
    record as { sabilTakhmeenCurrent?: { startDate?: unknown } | null } | undefined
  )?.sabilTakhmeenCurrent?.startDate;
  return formatListDate(startDate as string | number | Date | null | undefined, { empty: "—" });
}
